import { useCallback, useEffect, useState } from "react";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  RecordContextProvider,
  SelectField,
  TextField,
  Button,
  SimpleForm,
  required,
  useNotify,
  useRecordContext,
} from "react-admin";
import { useNavigate } from "react-router";
import { Box, CircularProgress, Grid } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import { DoctoralRankingStatusChoices } from "utils/choices";
import { DoctoralRankingStatus } from "utils/constants";
import DoctoralGenerateRankingButton from "./DoctoralGenerateRankingButton";
import { useNotifyError } from "utils/useNotifyError";
import { settings } from "app";
import DoctoralDateField from "../../Partials/DoctoralDateField";

const RankingRowButtons = ({ onChanged }) => {
  const record = useRecordContext();
  const navigate = useNavigate();

  if (!record) return null;

  const detailsUrl = doctoralRequests.campaignRanking(record.id);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
      <DoctoralGenerateRankingButton onDone={onChanged} />
      <SecuredComponent permission={permissions.canPreviewDoctoralCampaignRankings}>
        <Button
          label="Детайли"
          onClick={() => navigate(`/${detailsUrl}/show`)}
          variant="outlined"
          size="medium"
          sx={{ display: record.status === DoctoralRankingStatus.Pending ? "none" : "flex" }}
        >
          <LinkIcon />
        </Button>
      </SecuredComponent>
    </Box>
  );
};

const RankingsSection = () => {
  const notifyError = useNotifyError();
  const campaign = useRecordContext();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const campaignId = campaign?.id;

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        doctoralRequests.campaignRankingsByCampaign(campaignId),
        null,
        "GET",
      );
      setRankings((Array.isArray(data) ? data : []).map((x, i) => ({ ...x, index: `#${i + 1}` })));
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async formData => {
    try {
      await dataProvider.customRequest(
        doctoralRequests.createCampaignRanking(campaignId),
        {
          rankingDate: formData.rankingDate,
          enrollmentStartDate: formData.enrollmentStartDate,
          enrollmentEndDate: formData.enrollmentEndDate,
        },
        "POST",
      );
      notify("Успешно добавихте класиране", { type: "success" });
      setIsEditMode(false);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  if (!campaign) return null;

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );

  if (isEditMode) {
    return (
      <SimpleForm
        toolbar={false}
        onSubmit={handleSubmit}
      >
        <Grid
          container
          spacing={2}
          mb={2}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <CustomDateTimeInput
              source="rankingDate"
              label="Дата на класиране"
              validate={required()}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <CustomDateTimeInput
              source="enrollmentStartDate"
              label="Начало на записване"
              validate={required()}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <CustomDateTimeInput
              source="enrollmentEndDate"
              label="Край на записване"
              validate={required()}
            />
          </Grid>
        </Grid>
        <Box sx={{ textAlign: "left" }}>
          <Button
            label="Откажи"
            onClick={() => setIsEditMode(false)}
            variant="outlined"
            color="error"
            size="medium"
            startIcon={<CloseIcon />}
            sx={{ mr: 10 }}
          />
          <Button
            label="Запази"
            type="submit"
            color="primary"
            variant="contained"
            size="medium"
            startIcon={<SaveIcon />}
          />
        </Box>
      </SimpleForm>
    );
  }

  return (
    <RecordContextProvider value={{ id: campaignId, rankings }}>
      <ArrayField source="rankings">
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={settings.style.secondaryDatagrid}
          empty={<em>Няма изготвени класирания</em>}
        >
          <TextField
            label="№"
            source="index"
            sortable={false}
          />
          <DoctoralDateField
            source="rankingDate"
            label="Дата на класиране"
            showTime
           
            sortable={false}
          />
          <DoctoralDateField
            source="enrollmentStartDate"
            label="Начало на записване"
            showTime
           
            sortable={false}
          />
          <DoctoralDateField
            source="enrollmentEndDate"
            label="Край на записване"
            showTime
           
            sortable={false}
          />
          <SelectField
            source="status"
            label="Статус"
            choices={DoctoralRankingStatusChoices}
            sortable={false}
          />
          <FunctionField
            label=""
            render={() => <RankingRowButtons onChanged={load} />}
          />
        </Datagrid>
      </ArrayField>
      <SecuredComponent permission={permissions.canManageDoctoralCampaignRankings}>
        <Button
          startIcon={<AddCircleIcon />}
          variant="contained"
          size="medium"
          sx={{ mt: 2 }}
          label="Добави"
          onClick={() => setIsEditMode(true)}
        />
      </SecuredComponent>
    </RecordContextProvider>
  );
};

export default RankingsSection;
